import { useState, useCallback } from 'react';
import type {
  RoadTrip,
  RoadTripWaypoint,
  RoadTripSegment,
  RoadTripPreferences,
  OptimizedRoute,
  FuelStation,
  Attraction,
  RoadTripCostBreakdown,
  TripDay,
  Location
} from '../types/roadTrip';
import { differenceInDays, addHours, addDays, format, parseISO } from 'date-fns';

interface UseRoadTripOptions {
  defaultPreferences?: Partial<RoadTripPreferences>;
}

interface UseRoadTripReturn {
  trip: RoadTrip | null;
  setTrip: (trip: RoadTrip | null | ((prev: RoadTrip | null) => RoadTrip | null)) => void;
  preferences: RoadTripPreferences;
  updatePreferences: (updates: Partial<RoadTripPreferences>) => void;
  addWaypoint: (waypoint: Omit<RoadTripWaypoint, 'id' | 'order'>) => void;
  removeWaypoint: (waypointId: string) => void;
  reorderWaypoints: (waypointIds: string[]) => void;
  updateWaypoint: (waypointId: string, updates: Partial<RoadTripWaypoint>) => void;
  optimizeRoute: () => OptimizedRoute | null;
  calculateSegment: (start: RoadTripWaypoint, end: RoadTripWaypoint) => RoadTripSegment | null;
  calculateTotals: () => void;
  generateDayByDay: () => TripDay[];
  findFuelStops: () => FuelStation[];
  findAttractions: () => Attraction[];
  getCostBreakdown: () => RoadTripCostBreakdown;
}

const defaultPreferences: RoadTripPreferences = {
  maxDrivingHoursPerDay: 6,
  maxDistancePerDay: 500,
  avoidTolls: false,
  avoidHighways: false,
  preferredRoadTypes: ['highway', 'scenic'],
  restStopFrequency: 2,
  overnightStops: true,
  hotelBookingRequired: false,
  attractionsEnabled: true,
  attractionTypes: ['natural', 'cultural', 'scenic'],
  scenicStopsEnabled: true
};

export function useRoadTrip(options: UseRoadTripOptions = {}): UseRoadTripReturn {
  const { defaultPreferences: prefs = {} } = options;

  const [trip, setTrip] = useState<RoadTrip | null>(null);
  const [preferences, setPreferences] = useState<RoadTripPreferences>({
    ...defaultPreferences,
    ...prefs
  });

  const addWaypoint = useCallback((waypoint: Omit<RoadTripWaypoint, 'id' | 'order'>) => {
    if (!trip) return;

    const newWaypoint: RoadTripWaypoint = {
      ...waypoint,
      id: `waypoint_${Date.now()}`,
      order: trip.waypoints.length
    };

    setTrip(prev => prev ? {
      ...prev,
      waypoints: [...prev.waypoints, newWaypoint]
    } : null);
  }, [trip]);

  const removeWaypoint = useCallback((waypointId: string) => {
    if (!trip) return;

    setTrip(prev => prev ? {
      ...prev,
      waypoints: prev.waypoints.filter(w => w.id !== waypointId)
    } : null);
  }, [trip]);

  const reorderWaypoints = useCallback((waypointIds: string[]) => {
    if (!trip) return;

    const waypointMap = new Map(trip.waypoints.map(w => [w.id, w]));
    const reordered = waypointIds
      .map((id, index) => {
        const waypoint = waypointMap.get(id);
        return waypoint ? { ...waypoint, order: index } : null;
      })
      .filter((w): w is RoadTripWaypoint => w !== null);

    setTrip(prev => prev ? {
      ...prev,
      waypoints: reordered
    } : null);
  }, [trip]);

  const updateWaypoint = useCallback((waypointId: string, updates: Partial<RoadTripWaypoint>) => {
    if (!trip) return;

    setTrip(prev => prev ? {
      ...prev,
      waypoints: prev.waypoints.map(w =>
        w.id === waypointId ? { ...w, ...updates } : w
      )
    } : null);
  }, [trip]);

  const updatePreferences = useCallback((updates: Partial<RoadTripPreferences>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  }, []);

  const optimizeRoute = useCallback((): OptimizedRoute | null => {
    if (!trip || trip.waypoints.length < 2) return null;

    const required = trip.waypoints.filter(w => w.isRequired);
    const optional = trip.waypoints.filter(w => !w.isRequired);
    let optimized: RoadTripWaypoint[] = [...required];
    const remaining = [...optional];
    let current = required[required.length - 1] || trip.waypoints[0];

    while (remaining.length > 0) {
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      remaining.forEach((waypoint, index) => {
        const distance = calculateDistance(
          current.location.lat,
          current.location.lng,
          waypoint.location.lat,
          waypoint.location.lng
        );
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      const nearest = remaining.splice(nearestIndex, 1)[0];
      optimized.push(nearest);
      current = nearest;
    }

    const segments: RoadTripSegment[] = [];
    let totalDistance = 0;
    let totalDuration = 0;

    for (let i = 0; i < optimized.length - 1; i++) {
      const segment = calculateSegment(optimized[i], optimized[i + 1]);
      if (segment) {
        segments.push(segment);
        totalDistance += segment.distance;
        totalDuration += segment.duration;
      }
    }

    const directDistance = calculateDistance(
      optimized[0].location.lat,
      optimized[0].location.lng,
      optimized[optimized.length - 1].location.lat,
      optimized[optimized.length - 1].location.lng
    );
    const efficiency = directDistance > 0 ? Math.min(directDistance / totalDistance, 1) : 1;

    const suggestions: string[] = [];
    if (totalDuration > preferences.maxDrivingHoursPerDay * (optimized.length - 1)) {
      suggestions.push('Consider breaking this trip into multiple days');
    }

    return {
      waypoints: optimized,
      segments,
      totalDistance,
      totalDuration,
      efficiency,
      suggestions
    };
  }, [trip, preferences]);

  const calculateSegment = useCallback((start: RoadTripWaypoint, end: RoadTripWaypoint): RoadTripSegment | null => {
    const distance = calculateDistance(
      start.location.lat,
      start.location.lng,
      end.location.lat,
      end.location.lng
    );

    const avgSpeed = preferences.avoidHighways ? 50 : 80;
    const duration = distance / avgSpeed;
    const tolls = preferences.avoidTolls ? 0 : distance * 0.05;
    const fuelEfficiency = 12;
    const fuelCapacity = 50;
    const range = fuelCapacity * (100 / fuelEfficiency);
    const fuelStops = Math.floor(distance / range);
    const estimatedFuelCost = (distance / 100) * fuelEfficiency * 1.5;

    return {
      id: `segment_${start.id}_${end.id}`,
      startWaypointId: start.id,
      endWaypointId: end.id,
      distance: Math.round(distance),
      duration: Math.round(duration * 10) / 10,
      route: [],
      roadCondition: 'good',
      tolls: Math.round(tolls * 100) / 100,
      fuelStops,
      estimatedFuelCost: Math.round(estimatedFuelCost * 100) / 100
    };
  }, [preferences]);

  const calculateTotals = useCallback(() => {
    if (!trip) return;

    const segments = trip.waypoints.length > 1
      ? trip.waypoints.slice(0, -1).map((w, i) => calculateSegment(w, trip.waypoints[i + 1]))
      : [];

    const validSegments = segments.filter((s): s is RoadTripSegment => s !== null);
    const totalDistance = validSegments.reduce((sum, s) => sum + s.distance, 0);
    const totalDuration = validSegments.reduce((sum, s) => sum + s.duration, 0);
    const estimatedFuelCost = validSegments.reduce((sum, s) => sum + s.estimatedFuelCost, 0);
    const estimatedTollCost = validSegments.reduce((sum, s) => sum + s.tolls, 0);
    const totalCost = estimatedFuelCost + estimatedTollCost;

    setTrip(prev => prev ? {
      ...prev,
      totalDistance,
      totalDuration,
      estimatedFuelCost,
      estimatedTollCost,
      totalCost,
      segments: validSegments
    } : null);
  }, [trip, calculateSegment]);

  const generateDayByDay = useCallback((): TripDay[] => {
    if (!trip || trip.waypoints.length < 2) return [];

    const days: TripDay[] = [];
    const startDate = parseISO(trip.startDate);
    let currentDate = startDate;
    let currentWaypointIndex = 0;
    let accumulatedDrivingTime = 0;

    while (currentWaypointIndex < trip.waypoints.length - 1) {
      const dayStops: TripDay['stops'] = [];
      let dayDistance = 0;
      let dayDrivingTime = 0;
      let currentWaypoint = trip.waypoints[currentWaypointIndex];
      let nextIndex = currentWaypointIndex + 1;

      while (nextIndex < trip.waypoints.length) {
        const nextWaypoint = trip.waypoints[nextIndex];
        const segment = calculateSegment(currentWaypoint, nextWaypoint);

        if (!segment) break;

        if (accumulatedDrivingTime + segment.duration > preferences.maxDrivingHoursPerDay ||
          dayDistance + segment.distance > preferences.maxDistancePerDay) {
          break;
        }

        dayStops.push({
          waypoint: nextWaypoint,
          arrivalTime: addHours(currentDate, accumulatedDrivingTime + segment.duration).toISOString(),
          departureTime: addHours(currentDate, accumulatedDrivingTime + segment.duration + nextWaypoint.duration).toISOString(),
          activities: [`Visit ${nextWaypoint.location.name}`]
        });

        dayDistance += segment.distance;
        dayDrivingTime += segment.duration;
        accumulatedDrivingTime += segment.duration + nextWaypoint.duration;
        currentWaypointIndex = nextIndex;
        currentWaypoint = nextWaypoint;
        nextIndex++;
      }

      const dayEndLocation = trip.waypoints[currentWaypointIndex];

      days.push({
        date: format(currentDate, 'yyyy-MM-dd'),
        startLocation: trip.waypoints[0].location,
        endLocation: dayEndLocation.location,
        distance: dayDistance,
        drivingTime: dayDrivingTime,
        stops: dayStops,
        totalStops: dayStops.length,
        estimatedCost: dayDrivingTime * 15
      });

      if (currentWaypointIndex < trip.waypoints.length - 1) {
        currentDate = addDays(currentDate, 1);
        accumulatedDrivingTime = 0;
        currentWaypointIndex++;
      }
    }

    return days;
  }, [trip, preferences, calculateSegment]);

  const findFuelStops = useCallback((): FuelStation[] => {
    return [
      {
        id: 'fuel_1',
        name: 'Shell Gas Station',
        location: { name: 'Gas Station', address: 'Highway 1', lat: 34.05, lng: -118.25 },
        fuelType: 'gasoline',
        price: 1.45,
        currency: 'USD',
        distance: 5,
        open24Hours: true,
        amenities: ['Restroom', 'Snacks', 'Car Wash']
      }
    ];
  }, []);

  const findAttractions = useCallback((): Attraction[] => {
    return [
      {
        id: 'attr_1',
        name: 'Scenic Overlook',
        location: { name: 'Scenic Viewpoint', address: 'Mountain Road', lat: 34.10, lng: -118.20 },
        type: 'scenic',
        rating: 4.5,
        reviewCount: 250,
        description: 'Beautiful viewpoint with panoramic mountain views',
        detourDistance: 2,
        duration: 0.5,
        openingHours: '6:00 AM - 8:00 PM'
      }
    ];
  }, []);

  const getCostBreakdown = useCallback((): RoadTripCostBreakdown => {
    if (!trip) {
      return {
        fuel: 0, tolls: 0, accommodation: 0, food: 0,
        attractions: 0, maintenance: 0, total: 0,
        currency: 'USD', dailyAverage: 0
      };
    }

    const days = differenceInDays(parseISO(trip.endDate), parseISO(trip.startDate)) + 1;

    const breakdown: RoadTripCostBreakdown = {
      fuel: trip.estimatedFuelCost,
      tolls: trip.estimatedTollCost,
      accommodation: days * 100,
      food: days * 50,
      attractions: days * 30,
      maintenance: trip.totalDistance * 0.02,
      total: 0,
      currency: trip.currency,
      dailyAverage: 0
    };

    breakdown.total = breakdown.fuel + breakdown.tolls + breakdown.accommodation +
      breakdown.food + breakdown.attractions + breakdown.maintenance;
    breakdown.dailyAverage = breakdown.total / days;

    return breakdown;
  }, [trip]);

  return {
    trip, setTrip, preferences, updatePreferences,
    addWaypoint, removeWaypoint, reorderWaypoints, updateWaypoint,
    optimizeRoute, calculateSegment, calculateTotals, generateDayByDay,
    findFuelStops, findAttractions, getCostBreakdown
  };
}

function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

export default useRoadTrip;
